import React, { useState } from 'react';
import classNames from 'classnames';
import { formatDateString } from '../util';
import * as styles from '../CitationImportHistory.module.scss';
import { CitationImport } from 'types';
import {
  Button,
  Text,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Toggletip,
} from 'components/core';

type Props = {
  canUndo: boolean;
  undoDate?: string;
  onClick: () => void;
} & Pick<CitationImport, 'state'>;

export const UndoControl = ({ canUndo, state, undoDate, onClick }: Props) => {
  const [isModalOpen, setModalOpen] = useState(false);

  if (state === 'new' || state === 'started') {
    return <Text variant="weak">In progress</Text>;
  }

  if (state === 'undoing') {
    return <Text variant="weak">Undo in progress</Text>;
  }

  if (state === 'failed') {
    return (
      <Toggletip
        idPrefix="undo"
        triggerText="Undo not available"
        triggerVariant="weak"
        contentClassName={classNames(styles.undoToggletipContent)}
      >
        This import failed, so it cannot be undone.
      </Toggletip>
    );
  }

  if (undoDate) {
    return <Text variant="weak">Undone on {formatDateString(undoDate)}</Text>;
  }

  if (canUndo) {
    const button_text =
      state === 'undo_failed' ? 'Try undo import again' : 'Undo import';
    return (
      <>
        {state === 'undo_failed' && (
          <Text variant="weak" tabIndex={0} component={'span'}>
            Undo failed
          </Text>
        )}
        <Button
          type="neutral"
          variant="outline"
          onClick={() => {
            setModalOpen(true);
          }}
        >
          {button_text}
        </Button>
        <Modal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          size="sm"
        >
          <ModalHeader>Undo import?</ModalHeader>
          <ModalBody>
            This action will remove all studies added from this import.
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => setModalOpen(false)}
              type="neutral"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                onClick();
                setModalOpen(false);
              }}
              type="danger"
              data-testid="undo-confirm"
            >
              Undo Import
            </Button>
          </ModalFooter>
        </Modal>
      </>
    );
  }

  const undoStateText =
    state === 'undo_failed'
      ? 'Undo failed, and is now disabled'
      : 'Undo import disabled';

  return (
    <Toggletip
      idPrefix="undo"
      triggerText={undoStateText}
      triggerVariant="weak"
      contentClassName={classNames(styles.undoToggletipContent)}
    >
      Import cannot be undone either because actions have occurred against one
      or more studies, or because another import is currently in the process of
      being undone.
    </Toggletip>
  );
};

export default UndoControl;
